/**
 * @fileoverview The controls above the locations: a search, the three densities, and the
 * way to the archive.
 *
 * Deliberately smaller than the plant toolbar. Locations are few and do not
 * need ordering or filtering by stage; a search that also finds a bed by the
 * plants in it covers what people actually look for. What is typed
 * goes to the server (W24 Job H: LocationGrid's useBedSearch, after the usual
 * typing pause); this box only holds the text and says how many beds the
 * answer left.
 *
 * Where: the top of LocationGrid.
 * API: none of its own (the search is locationViews' useBedSearch).
 * Words (en/es): locations.toolbar.* (search, clearSearch, view,
 * viewCards, viewGrid, viewList, archive, showing).
 *
 * @module LocationToolbar
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Archive, Search, X } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { ViewSwitch } from '@/shared/components/ViewSwitch';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { LocationViewSettings } from '../lib/locationViews';

/** What the toolbar shows and where its changes go. */
interface LocationToolbarProps {
  /** The density and the search as they stand. */
  settings: LocationViewSettings;
  /** Changes either. */
  onChange: (patch: Partial<LocationViewSettings>) => void;
  /** How many locations the search leaves, out of how many there are. */
  shown: number;
  /** How many locations there are before the search. */
  total: number;
}

/**
 * The search, density switch and archive link above the locations; says how many the search leaves.
 *
 * @param props - LocationToolbarProps.
 */
export const LocationToolbar: React.FC<LocationToolbarProps> = ({ settings, onChange, shown, total }) => {
  const { reactTranslate: t } = useTranslation();
  const filtered = shown !== total;

  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={settings.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder={t('locations.toolbar.search')}
            aria-label={t('locations.toolbar.search')}
            className="h-9 pl-8 pr-8"
          />
          {settings.search && (
            <button
              type="button"
              onClick={() => onChange({ search: '' })}
              aria-label={t('locations.toolbar.clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

        <ViewSwitch
          value={settings.view}
          onChange={(view) => onChange({ view })}
          label={t('locations.toolbar.view')}
          names={{
            cards: t('locations.toolbar.viewCards'),
            grid: t('locations.toolbar.viewGrid'),
            list: t('locations.toolbar.viewList'),
          }}
        />

        <Link
          to="/beds/archive"
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-sm text-muted-foreground hover:text-foreground"
          title={t('locations.toolbar.archive')}
        >
          <Archive className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">{t('locations.toolbar.archive')}</span>
        </Link>
      </div>

      {filtered && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {t('locations.toolbar.showing', { shown, total })}
        </p>
      )}
    </div>
  );
};
